import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState(null);
  const [subLoading, setSubLoading] = useState(false);

  const loadSub = useCallback(async (u) => {
    if (!u || u.is_demo || u.is_platform_admin) { setSub(null); return; }
    setSubLoading(true);
    try { const { data } = await api.get("/billing/subscription"); setSub(data); }
    catch { setSub(null); }
    finally { setSubLoading(false); }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("pg_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then(async (r) => { setUser(r.data); await loadSub(r.data); })
      .catch(() => localStorage.removeItem("pg_token"))
      .finally(() => setLoading(false));
  }, [loadSub]);

  const finishAuth = async (data) => {
    localStorage.setItem("pg_token", data.token);
    setUser(data.user);
    await loadSub(data.user);
    return data.user;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.twofa_required) return data;
    return finishAuth(data);
  };
  const verify2fa = async (payload) => finishAuth((await api.post("/auth/2fa/verify", payload)).data);
  const register = async (payload) => finishAuth((await api.post("/auth/register", payload)).data);
  const demoLogin = async () => finishAuth((await api.post("/auth/demo")).data);
  const refreshSub = () => loadSub(user);

  const setRole = async (role) => {
    const { data } = await api.put("/users/me/role", { role });
    setUser((u) => ({ ...u, role: data.role, permissions: data.permissions }));
    return data;
  };
  const can = useCallback((perm) => !!user && (user.permissions || []).includes(perm), [user]);

  const logout = () => {
    localStorage.removeItem("pg_token");
    setUser(null);
    window.location.href = "/login";
  };

  const exempt = user && (user.is_demo || user.is_platform_admin);
  const needsSubscription = !!user && !exempt && (sub == null || sub.status !== "actif");

  return (
    <AuthContext.Provider value={{ user, loading, login, register, demoLogin, verify2fa, logout, sub, subLoading, needsSubscription, refreshSub, can, setRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
